import { defineComponent as _defineComponent } from 'vue'
import { ref as _ref, computed as _computed } from 'vue';

import { watch } from 'vue';
import { BaseSearchFilterDto } from '@/api';
import i18n from '@/i18n';
import SEllipsisTooltip from '@/components/SEllipsisTooltip/SEllipsisTooltip.vue';
import SearchFilterTreeviewSelector from './SearchFilterTreeviewSelector.vue';

interface SearchFilterTreeviewProps {
  filter?: BaseSearchFilterDto;
  tooltip?: string;
  hasSearch?: boolean;
  hideEmpty?: boolean;
  expanded?: boolean;
  value?: string[];
  /**
   * When false, the expansion panel header is hidden.
   * Use when embedding inside a parent expansion panel (e.g. Assignment group).
   * @default true
   */
  expandable?: boolean;
}

type SearchFilterTreeviewEmit = {
  (event: 'input', items: string[]): void;
}

const MAX_ITEMS = 50

export default /*#__PURE__*/_defineComponent({
  __name: 'SearchFilterTreeview',
  props: {
  filter: null,
  tooltip: null,
  hasSearch: { type: Boolean },
  hideEmpty: { type: Boolean },
  expanded: { type: Boolean },
  value: null,
  expandable: { type: Boolean, default: true }
},
  emits: ["input"],
  setup(__props, { emit: emits }) {

const props = __props;






const search = _ref('');
let isLoading = _ref(true);
let isLazyLoading = _ref(true);
// Count of valid selections (i.e. selections that exist in the tree), for consistent display.
let validSelectionCount = _ref(0);

const items = _computed(() => {
  if (!props.filter?.children) return [];

  const itemsArray = props.filter?.children?.map((item) => {
    let name = '';
    if ('name' in item) name = item.name || i18n.t(`tree_selector.${item.id}`);
    return { ...item, name };
  }) || [];
  isLoading.value = false;
  return itemsArray;
});

const selectedItems = _computed(() => props.value || []);

const hasResults = _computed(() => !!items.value?.length);

/**
 * Recursively collect all selectable IDs from the tree (root + nested children).
 * @param nodes - The nodes tree to collect IDs from.
 */
function collectAllIds(nodes: Array<{ id?: string; children?: unknown[] }>): Set<string> {
  const set = new Set<string>();
  for (const node of nodes) {
    const { id = '', children } = node;
    if (id) set.add(id);
    if (Array.isArray(children) && children.length > 0) {
      collectAllIds(children as Array<{ id?: string; children?: unknown[] }>).forEach((childId) => set.add(childId));
    }
  }
  return set;
}

/**
 * Update the valid selection count when the filter options or selection changes.
 */
watch(
  () => [props.filter?.children, props.value] as const,
  () => {
    const { filter } = props;
    if (!filter) return;
    const current = props.value ?? [];
    const { children } = filter;
    const hasOptions = Array.isArray(children) && children.length > 0;
    if (hasOptions) {
      const ids = collectAllIds(children as Array<{ id?: string; children?: unknown[] }>);
      const valid = current.filter((id) => ids.has(id));
      validSelectionCount.value = Math.min(valid.length, current.length);
    } else {
      validSelectionCount.value = 0;
    }
  },
  { immediate: true },
);

const displayFilter = _computed(() => {
  if (!items.value?.length && props.hideEmpty) {
    return false;
  }
  return true;
});

const filterTitle = _computed(() => {
  if (!props.filter) return '';
  if (props.filter?.type === BaseSearchFilterDto.type.CUSTOM_METADATA) {
    return props.filter?.query || '';
  }
  return i18n.t(`tree_selector.${props.filter.type}`);
});
// Only show count when there are selectable items; avoids phantom count when API returns empty filter.
const selectedItemCount = _computed(() => (hasResults.value ? Math.min(selectedItems.value?.length, validSelectionCount.value) : 0) ?? 0);
const type = _computed(() => props.filter?.type!);
const expandedState = _computed(() => {
  if (!props.expandable) return 0;
  return props.expanded ? 0 : 1;
});
const shouldLazyLoad = _computed(() => items.value?.length > MAX_ITEMS && props.expandable && !props.expanded);

const handleUpdateSelectedItems = (newSelected: string[]) => {
  emits('input', newSelected);
};

const handleUpdateLazyLoadingState = (newLoadingState: boolean) => {
  isLazyLoading.value = newLoadingState;
};

return { __sfc: true,MAX_ITEMS, props, emits, search, isLoading, isLazyLoading, validSelectionCount, items, selectedItems, hasResults, collectAllIds, displayFilter, filterTitle, selectedItemCount, type, expandedState, shouldLazyLoad, handleUpdateSelectedItems, handleUpdateLazyLoadingState, _ref, _computed, watch, BaseSearchFilterDto, i18n, SEllipsisTooltip, SearchFilterTreeviewSelector }
}

})